import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Address } from '@returnmates/client-core/src/graphql/generated/api'
import { getServicedZipCodes as getServicedZipCodesSelector } from '@returnmates/client-core/src/selectors/zipCode'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { PickupDetails } from '@returnmates/client-core/src/type/pickups'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import detailsValidate from '@returnmates/client-core/src/utils/validation/pickupHelpers/details'
import Button from '@returnmates/ui-core/src/components/Button'
import ErrorBlock from '@returnmates/ui-core/src/components/ErrorBlock'
import Details from '@returnmates/ui-core/src/components/PickupDetails'
import useOutsideClick from '@returnmates/ui-core/src/hooks/useOutsideClick'
import clsx from 'clsx'
import { FORM_ERROR } from 'final-form'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { CONSOLIDATIONS } from '../../constants/paths'
import useStyles from './styles'

interface Props {
  className?: string
  onCloseDropdown: () => void
  createAddress: (val: PickupDetails) => Promise<Address>
  reset: (res: Address) => void
}

function AddNewAddress({ className, onCloseDropdown, createAddress, reset }: Props) {
  const classes = useStyles()
  const location = useLocation()
  const addressDropdownRef = useRef(null)
  const dispatch = useDispatch()
  const servicedZipCodes = useSelector(getServicedZipCodesSelector)
  const [isLoading, setIsLoading] = useState(false)
  useOutsideClick(addressDropdownRef, onCloseDropdown)

  const isConsolidationAdress = useMemo(() => location.pathname.includes(CONSOLIDATIONS), [
    location.pathname,
  ])

  const handleAddressCreate = useCallback(
    async (val: PickupDetails) => {
      setIsLoading(true)
      try {
        const res = await createAddress(val)

        if (res) {
          reset(res)
          onCloseDropdown()
        }
      } catch (err) {
        const { message } = err as Error

        return { [FORM_ERROR]: errorMapper(message || (err as string)) }
      } finally {
        setIsLoading(false)
      }
    },
    [onCloseDropdown, createAddress, reset],
  )

  const validate = useCallback(
    val => detailsValidate(val, { email: true, zipCode: Boolean(isConsolidationAdress) }),
    [isConsolidationAdress],
  )

  const getServicedZipCodes = useCallback(async () => {
    if (!Object.keys(servicedZipCodes).length) {
      try {
        setIsLoading(true)
        await createAsyncAction(dispatch, actions.getServicedZipCodes.request({ where: {} }))
      } catch (err) {
        const { message } = err as Error
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        setIsLoading(false)
      }
    }
  }, [dispatch, servicedZipCodes])

  useEffect(() => {
    getServicedZipCodes()
  }, [getServicedZipCodes])

  return (
    <div className={clsx(className, classes.newAddressDropdown)} ref={addressDropdownRef}>
      <Form
        onSubmit={handleAddressCreate}
        validate={validate}
        render={({ handleSubmit, submitError }) => (
          <>
            <ErrorBlock message={submitError} className={classes.submitError} />
            <Details isModal showNotificationWarning={false} autoFocus />
            <div className={classes.modalButtons}>
              <Button
                className={clsx(classes.button, classes.cancelButton, classes.modalCancelButton)}
                onClick={onCloseDropdown}
                label="Cancel"
                isLoading={isLoading}
              />
              <Button
                className={clsx(classes.button, classes.saveButton, classes.modalSaveButton)}
                onClick={handleSubmit}
                label="Save address"
                isLoading={isLoading}
              />
            </div>
          </>
        )}
      />
    </div>
  )
}

export default memo(AddNewAddress)
